import React, { useState } from "react";
import {
  AnalyticsCard,
  AnalyticsTable,
  Cell,
  ColumnDef,
  DropdownFilter,
  LeadsRadialPieChart,
  metricsToRadialSlices,
  TABLE_MIN_HEIGHT,
  TableFooter,
} from "../../common";
import {
  usePageAnalyticsChartQuery,
  usePageAnalyticsQuery,
} from "src/query/experiences/leadsAndReports/reports";
import { useTableState } from "../../hooks/useTableState";
import { formatDurationWithLabel, safeDivide } from "../../utils/format";
import { isInteractive } from "../../constants";
import { ArrangeOption } from "../../common/DropdownFilter";
import styles from "../../common/common.module.scss";

interface PageAnalyticsProps {
  storyboardId: string;
  storyboardType?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "name", header: "Page Name" },
  { key: "views", header: "Views", align: "right" },
  { key: "visitors", header: "Visitors", align: "right" },
  { key: "total", header: "Total Time", align: "right" },
  { key: "avg", header: "Avg. Time", align: "right" },
  { key: "bounce", header: "Bounce Rate", align: "right" },
];

const PIE_OPTIONS = [
  { label: "Visitors", value: "visitors" },
  { label: "Views", value: "views" },
  { label: "Time", value: "timeSpent" },
];

export const PageAnalytics: React.FC<PageAnalyticsProps> = ({ storyboardId, storyboardType }) => {
  const { request, key, page, setPage, chart, setChart, enabled } = useTableState(storyboardId);
  const typeReady = storyboardType !== undefined;
  const variant = isInteractive(storyboardType) ? "interactive" : "landing";
  const { data, isLoading } = usePageAnalyticsQuery(variant, request, key, page, enabled && typeReady);
  const rows = typeReady ? (data?.records ?? []) : [];

  const [viewOption, setViewOption] = useState<ArrangeOption>({
    label: "Views",
    value: "views",
    name: "name",
  });

  const { data: chartData, isLoading: isChartLoading } = usePageAnalyticsChartQuery(
    variant,
    request,
    key,
    enabled && typeReady && chart === "pie_chart_view",
  );
  const pieData = chartData?.records ?? [];

  const renderChart = () => {
    switch (chart) {
      case "pie_chart_view":
        return (
          <div className={styles.chartFull}>
            <LeadsRadialPieChart
              data={metricsToRadialSlices(pieData, viewOption)}
              measureLabel={viewOption.label}
              height={280}
              loading={isChartLoading}
            />
          </div>
        );
      case "table_view":
        return (
          <AnalyticsTable
            variant="split"
            columns={COLUMNS}
            rows={rows}
            loading={isLoading || !typeReady}
            renderRow={(row: any) => (
              <>
                <Cell headline={row.name} />
                <Cell headline={row.views} />
                <Cell headline={row.visitors} />
                <Cell headline={formatDurationWithLabel(row.timeSpent).value} />
                <Cell headline={formatDurationWithLabel(safeDivide(row.timeSpent, row.views)).value} />
                <Cell headline={`${Math.round(row.bounceRate)}%`} />
              </>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title="Page Analytics"
      count={typeReady ? (data?.totalRecordsCount ?? rows.length) : undefined}
      currentChart={chart}
      disableView={["graph_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.split}
      action={
        chart === "pie_chart_view" ? (
          <DropdownFilter
            arrangeOption={viewOption}
            handleArrange={(o) => setViewOption((s) => ({ ...s, ...o }))}
            options={PIE_OPTIONS}
            disableOption={[]}
          />
        ) : undefined
      }
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Pages"
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
